import {
  RISK_INJURY_VALUES,
  RISK_FREQUENCY_VALUES,
  RISK_AVOIDANCE_VALUES,
  RISK_LIKELIHOOD_VALUES,
} from '../constants/riskAssessmentEnums'

/**
 * Calculates risk score based on injury, frequency, avoidance, and likelihood.
 * Must stay in sync with server app/utils/calculateRiskScore.
 *
 * Mapping: DEATH P2→9 P1→8; SERIOUS F2 P2 L2→8, F2 P2 L1→7, F2 P1→6, F1 P2→6, F1 P1→5;
 * S1 F2 P2→5, F2 P1→4, F1 P2→3, F1 P1 L2→2, F1 P1 L1→1.
 */
export function calculateRiskScore(
  injury: string | null,
  frequency: string | null,
  avoidance: string | null,
  likelihood: string | null
): number | null {
  if (!injury || !frequency || !avoidance || !likelihood) {
    return null
  }

  if (injury === 'NO_INJURY') {
    return 0
  }

  if (injury === 'DEATH') {
    if (avoidance === 'HARDLY_POSSIBLE') {
      return 9
    }
    if (avoidance === 'POSSIBLE') {
      return 8
    }
    return null
  }

  if (injury === 'SERIOUS_INJURY') {
    if (
      frequency === 'OFTEN' &&
      avoidance === 'HARDLY_POSSIBLE' &&
      likelihood === 'HIGH'
    ) {
      return 8
    }
    if (
      frequency === 'OFTEN' &&
      avoidance === 'HARDLY_POSSIBLE' &&
      likelihood === 'LOW'
    ) {
      return 7
    }
    if (
      frequency === 'OFTEN' &&
      avoidance === 'POSSIBLE' &&
      likelihood === 'HIGH'
    ) {
      return 6
    }
    if (
      frequency === 'OFTEN' &&
      avoidance === 'POSSIBLE' &&
      likelihood === 'LOW'
    ) {
      return 6
    }
    if (frequency === 'RARELY' && avoidance === 'HARDLY_POSSIBLE') {
      return 6
    }
    if (frequency === 'RARELY' && avoidance === 'POSSIBLE') {
      return 5
    }
    return null
  }

  if (injury === 'SLIGHT_REVERSIBLE_INJURY') {
    if (frequency === 'OFTEN' && avoidance === 'HARDLY_POSSIBLE') {
      return 5
    }
    if (frequency === 'OFTEN' && avoidance === 'POSSIBLE') {
      return 4
    }
    if (frequency === 'RARELY' && avoidance === 'HARDLY_POSSIBLE') {
      return 3
    }
    if (
      frequency === 'RARELY' &&
      avoidance === 'POSSIBLE' &&
      likelihood === 'HIGH'
    ) {
      return 2
    }
    if (
      frequency === 'RARELY' &&
      avoidance === 'POSSIBLE' &&
      likelihood === 'LOW'
    ) {
      return 1
    }
    return null
  }

  return null
}

export function getInjuryLabel(value: string | null): string {
  if (!value) {
    return 'N/A'
  }
  return RISK_INJURY_VALUES.find((item) => item.value === value)?.label ?? value
}

export function getAvoidanceLabel(value: string | null): string {
  if (!value) {
    return 'N/A'
  }
  return (
    RISK_AVOIDANCE_VALUES.find((item) => item.value === value)?.label ?? value
  )
}

export function getLikelihoodLabel(value: string | null): string {
  if (!value) {
    return 'N/A'
  }
  return (
    RISK_LIKELIHOOD_VALUES.find((item) => item.value === value)?.label ?? value
  )
}

export function getFrequencyLabel(value: string | null): string {
  if (!value) {
    return 'N/A'
  }
  return (
    RISK_FREQUENCY_VALUES.find((item) => item.value === value)?.label ?? value
  )
}

export function getRiskScoreTextColorClassName(score: number | null): string {
  if (score === null) {
    return 'text-muted-foreground'
  }
  if (score <= 1) {
    return 'text-green-600'
  }
  if (score <= 2) {
    return 'text-blue-600'
  }
  if (score <= 4) {
    return 'text-yellow-600'
  }
  if (score <= 6) {
    return 'text-orange-600'
  }
  return 'text-red-600'
}

export function getRiskScoreBackgroundColorClassName(
  score: number | null
): string {
  if (score === null) {
    return 'bg-muted-foreground'
  }
  if (score <= 1) {
    return 'bg-green-600'
  }
  if (score <= 2) {
    return 'bg-blue-600'
  }
  if (score <= 4) {
    return 'bg-yellow-600'
  }
  if (score <= 6) {
    return 'bg-orange-600'
  }
  return 'bg-red-600'
}
